import type { Action } from './actions';
import type { Tag } from './tags';
import type { AspectDef } from './skills';

/**
 * Evolution trees grafted onto the skills in `skills.ts`.
 * EXTRA_ASPECTS adds ring-one nodes; ASPECT_CHILDREN hangs ring two under a ring-one id.
 * Every skill ends with three ring-one options; signature skills and the Druid's summons carry
 * three ring-two evolutions under each of them.
 */

const P = (a: Partial<import('./actions').ProjectileAction> & { visual: string; tags: Tag[] }): Action => ({
  type: 'projectile',
  count: 1,
  spread: 0,
  pattern: 'aimed',
  speed: 520,
  range: 620,
  damage: 1,
  radius: 8,
  ...a,
});

const summon = (unit: string, count: number, duration: number, at: 'self' | 'aim' = 'self', unique = false): Action => ({ type: 'summon', unit, count, duration, at, unique });

export const EXTRA_ASPECTS: Record<string, AspectDef[]> = {
  // ---------------------------------------------------------------- Druid: the reference trees
  kindred_beast: [
    { id: 'shaggy_dragon', action: summon('shaggy_dragon', 1, 20, 'self', true), color: '#ffb347' },
    { id: 'three_bears', action: summon('lesser_bear', 3, 18, 'self', true), color: '#c8a070' },
    { id: 'armored_bear', action: summon('armored_bear', 1, 22, 'self', true), color: '#9fb4c8' },
  ],
  walking_tree: [
    { id: 'ram_tree', action: summon('ram_treant', 1, 20, 'self', true), color: '#8a6a3a' },
    { id: 'healer_tree', action: summon('heal_treant', 1, 22, 'self', true), color: '#b8e39a' },
    { id: 'fortress_tree', action: summon('fortress_treant', 1, 24, 'self', true), color: '#5a7a4a' },
  ],
  vine: [
    { id: 'snare_vine', action: summon('snare_vine', 1, 12, 'aim'), color: '#7fdcff' },
    { id: 'whip_vine', action: summon('whip_vine', 1, 12, 'aim'), color: '#8fce6a' },
    { id: 'shield_vine', action: summon('shield_vine', 1, 14, 'aim'), color: '#dfe9ff' },
  ],
  flytrap: [
    { id: 'devourer', action: summon('devourer_trap', 1, 14, 'aim'), color: '#c23f6a' },
    { id: 'venom_bush', action: summon('venom_bush', 1, 14, 'aim'), color: '#9ee37d' },
    { id: 'lure_trap', action: summon('lure_trap', 1, 14, 'aim'), color: '#ffd7a0' },
  ],
  // ---------------------------------------------------------------- signatures: third ring-one node
  wail: [{ id: 'wail_dirge', action: summon('shade_wailer', 2, 12), color: '#5a4aa8' }],
  light_volley: [{ id: 'volley_mercy', action: P({ count: 4, spread: 0.6, pierce: 1, damage: 0.8, speed: 640, range: 680, tags: ['holy', 'pierce', 'heal', 'projectile'], visual: 'arrow_light', onHit: { type: 'heal', target: 'lowest', radius: 400, power: 0.25 } }), color: '#fff7d6' }],
  hex: [{ id: 'hex_dread', action: { type: 'area', at: 'aim', radius: 140, damage: 0.2, tags: ['curse', 'area'], status: [{ id: 'weak', duration: 6 }, { id: 'vulnerable', duration: 6 }], visual: 'terror' }, color: '#8a6aff' }],
  bulwark_oath: [
    {
      id: 'oath_valor',
      action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'might', duration: 6 }, { id: 'haste', duration: 4 }], visual: 'cry' }, { type: 'buff', target: 'enemies', at: 'self', radius: 200, status: [{ id: 'taunt', duration: 3 }] }] },
      color: '#ffd166',
    },
  ],
  spirit_pact: [{ id: 'pact_wraith', action: summon('imp_wraith', 1, 12), color: '#b3a6ff' }],
  tiger_form: [
    {
      id: 'form_lion',
      action: { type: 'multi', actions: [{ type: 'buff', target: 'self', status: [{ id: 'tiger', duration: 9 }, { id: 'might', duration: 5 }], visual: 'haste' }, { type: 'buff', target: 'enemies', at: 'self', radius: 200, status: [{ id: 'taunt', duration: 3 }] }] },
      color: '#ffe38a',
    },
  ],
  attunement: [{ id: 'attune_twin', mods: [{ stat: 'projCount', add: 1 }, { stat: 'damage', inc: -0.1 }], color: '#c9f3ff' }],
  turret: [{ id: 'turret_tesla', action: summon('gob_tesla', 1, 12, 'aim'), color: '#a8f0ff' }],
  rallying_chord: [{ id: 'chord_war', action: { type: 'buff', target: 'allies', radius: 260, status: [{ id: 'might', duration: 6 }, { id: 'inspire', duration: 6 }], cleanse: true, visual: 'cry' }, color: '#ff9a3c' }],
  // ---------------------------------------------------------------- everyone else: third ring-one node
  fireball: [{ id: 'fireball_wildfire', action: P({ count: 3, spread: 0.5, damage: 1.0, radius: 8, speed: 460, tags: ['fire', 'projectile'], visual: 'fireball', onHit: { type: 'area', at: 'target', radius: 60, damage: 0.3, duration: 3, tickRate: 2, tags: ['fire', 'area'], status: [{ id: 'burn', duration: 3, stacks: 1 }], visual: 'zone_fire' } }), color: '#ff9a3c' }],
  frost_nova: [{ id: 'nova_permafrost', action: { type: 'area', at: 'self', radius: 170, damage: 0.8, tags: ['frost', 'area'], status: [{ id: 'chill', duration: 5, stacks: 5 }, { id: 'stun', duration: 0.5 }], visual: 'nova_frost' }, cooldownMul: 1.2, color: '#dff6ff' }],
  chain_lightning: [{ id: 'chain_static', action: { type: 'chain', jumps: 6, range: 260, damage: 0.9, tags: ['lightning'], status: [{ id: 'shock', duration: 4, stacks: 2 }], visual: 'chain' }, color: '#e8fbff' }],
  shield_bash: [{ id: 'bash_thunder', action: { type: 'dash', distance: 160, damage: 1.6, radius: 80, tags: ['blunt', 'lightning', 'movement'], status: [{ id: 'shock', duration: 3, stacks: 1 }, { id: 'stun', duration: 0.7 }], visual: 'bash' }, color: '#a8f0ff' }],
  blood_rite: [{ id: 'rite_offering', action: { type: 'multi', actions: [{ type: 'cost', hpPct: 0.3 }, { type: 'heal', target: 'allies', radius: 260, power: 0.8 }, { type: 'buff', target: 'allies', radius: 260, status: [{ id: 'might', duration: 5 }], visual: 'blood' }] }, color: '#ff9ab8' }],
  holy_ward: [{ id: 'ward_bastion', action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 200, status: [{ id: 'shield', duration: 8, power: 0.4 }, { id: 'fortify', duration: 8 }], visual: 'ward' }, { type: 'heal', target: 'allies', radius: 200, pct: 0.05 }] }, cooldownMul: 1.15, color: '#dfe9ff' }],
  void_rift: [{ id: 'rift_collapse', action: { type: 'area', at: 'aim', radius: 130, damage: 2.4, pull: 600, tags: ['void', 'area'], visual: 'explosion_void' }, color: '#b48cff' }],
  arrow_storm: [{ id: 'storm_barrage', action: { type: 'area', at: 'aim', radius: 110, damage: 0.9, duration: 2, tickRate: 6, tags: ['pierce', 'area'], visual: 'zone_arrows' }, cooldownMul: 0.9, color: '#e0e6b8' }],
  war_cry: [{ id: 'cry_dirge', action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 260, status: [{ id: 'regen', duration: 8, stacks: 2 }, { id: 'fortify', duration: 6 }], visual: 'cry' }, { type: 'buff', target: 'enemies', at: 'self', radius: 260, status: [{ id: 'weak', duration: 6 }] }] }, color: '#c8b8ff' }],
  orbit_blades: [{ id: 'blades_frost', action: P({ count: 3, pattern: 'ring', damage: 0.7, radius: 12, pierce: 99, speed: 0, range: 9999, tags: ['slash', 'frost', 'projectile'], status: [{ id: 'chill', duration: 2, stacks: 1 }], visual: 'blade', orbit: { radius: 85, duration: 5.5, angVel: 4.5 } }), color: '#bfefff' }],
  ember_aura: [{ id: 'aura_hellfire', action: { type: 'area', at: 'self', radius: 90, damage: 0.9, tags: ['fire', 'aura', 'area'], status: [{ id: 'burn', duration: 3, stacks: 1 }], visual: 'aura_fire_big' }, color: '#ff4d1a' }],
  frost_pulse: [{ id: 'pulse_glacier', action: { type: 'area', at: 'self', radius: 170, damage: 0.6, tags: ['frost', 'aura', 'area'], status: [{ id: 'chill', duration: 4, stacks: 2 }], visual: 'nova_frost' }, color: '#e0f7ff' }],
  spirit_bolt: [{ id: 'bolt_seeker', action: P({ count: 2, spread: 0.4, homing: 8, damage: 0.9, speed: 500, pattern: 'nearest', tags: ['void', 'projectile'], visual: 'bolt_void' }), color: '#d8c8ff' }],
  bone_shards: [{ id: 'shards_wall', action: P({ count: 9, pattern: 'ring', pierce: 1, damage: 0.5, speed: 500, range: 300, tags: ['pierce', 'projectile'], visual: 'shard_bone' }), color: '#efe6d2' }],
  blight_cloud: [{ id: 'cloud_spores', action: { type: 'area', at: 'self', radius: 150, damage: 0.35, duration: 4, tickRate: 2, tags: ['blight', 'aura', 'area'], status: [{ id: 'blight', duration: 4, stacks: 1 }, { id: 'weak', duration: 2 }], visual: 'zone_blight' }, color: '#b6e69a' }],
  guardian_totem: [{ id: 'totem_storm', action: summon('thunder_totem', 1, 12, 'aim'), color: '#a9dcff' }],
  mirror_shards: [{ id: 'shards_prism', action: P({ count: 5, spread: 1.2, pierce: 2, damage: 0.8, homing: 3, tags: ['slash', 'projectile'], visual: 'shard_mirror' }), color: '#ffffff' }],
  thornroot: [{ id: 'root_strangler', action: summon('strangler_root', 1, 14, 'aim'), color: '#6a8a4a' }],
  snare_trap: [{ id: 'trap_bloom', action: { type: 'area', at: 'aim', radius: 100, damage: 0.4, duration: 8, tickRate: 1, tags: ['void', 'area', 'heal'], status: [{ id: 'weak', duration: 3 }, { id: 'vulnerable', duration: 2 }], healAllies: 0.15, visual: 'zone_trap' }, color: '#ffd7f0' }],
};

export const ASPECT_CHILDREN: Record<string, AspectDef[]> = {
  // ---------------------------------------------------------------- Druid: kindred beast
  shaggy_dragon: [
    { id: 'plague_dragon', patch: { unit: 'plague_dragon' }, color: '#9ee37d' },
    { id: 'chaos_serpent', patch: { unit: 'chaos_serpent' }, color: '#c65cff' },
    { id: 'bonded_dragon', patch: { unit: 'bonded_dragon', duration: 9999 }, flags: ['bondRedirect'], color: '#ffe38a' },
  ],
  three_bears: [
    { id: 'bear_ring', patch: { unit: 'guard_bear' }, color: '#d9c9a3' },
    { id: 'bear_pack', patch: { unit: 'pack_bear' }, color: '#ff8c5c' },
    { id: 'mother_bear', action: { type: 'multi', actions: [summon('mother_bear', 1, 20, 'self', true), summon('bear_cub', 2, 20, 'self', true)] }, color: '#e8c39e' },
  ],
  armored_bear: [
    { id: 'banner_bear', patch: { unit: 'banner_bear' }, color: '#ffd166' },
    { id: 'crusher_bear', patch: { unit: 'crusher_bear' }, color: '#ff6a6a' },
    { id: 'loyal_bear', patch: { duration: 9999 }, mods: [{ stat: 'summonPower', inc: 0.15 }], color: '#8fe38a' },
  ],
  // ---------------------------------------------------------------- Druid: walking tree
  ram_tree: [
    { id: 'thorn_ram', patch: { unit: 'thorn_treant' }, color: '#c0304f' },
    { id: 'rootquake', patch: { unit: 'quake_treant' }, color: '#b39b6a' },
    { id: 'twin_rams', patch: { unit: 'ram_treant_lesser', count: 2 }, color: '#d8c8a0' },
  ],
  healer_tree: [
    { id: 'sap_tree', patch: { unit: 'sap_treant' }, color: '#7be0a0' },
    { id: 'bark_tree', patch: { unit: 'bark_treant' }, color: '#d9c9a3' },
    { id: 'elder_tree', patch: { unit: 'elder_treant', duration: 26 }, color: '#5a8a4a' },
  ],
  fortress_tree: [
    { id: 'hollow_fortress', patch: { unit: 'hollow_treant' }, color: '#dfe9ff' },
    { id: 'burning_fortress', patch: { unit: 'ember_treant' }, color: '#ff8c42' },
    { id: 'walking_wall', patch: { unit: 'wall_treant', count: 2 }, color: '#8a8a6a' },
  ],
  // ---------------------------------------------------------------- Druid: vine
  snare_vine: [
    { id: 'thorn_net', patch: { unit: 'thorn_net' }, color: '#ff6a6a' },
    { id: 'frost_net', patch: { unit: 'frost_net' }, color: '#bfefff' },
    { id: 'grasping_net', patch: { unit: 'grasping_net' }, color: '#8a7cff' },
  ],
  whip_vine: [
    { id: 'venom_whip', patch: { unit: 'venom_whip' }, color: '#9ee37d' },
    { id: 'twin_whips', patch: { count: 2 }, color: '#c8ff8a' },
    { id: 'long_whip', patch: { unit: 'long_whip' }, color: '#6fae5a' },
  ],
  shield_vine: [
    { id: 'barrier_vine', patch: { unit: 'barrier_vine' }, color: '#dfe9ff' },
    { id: 'mending_vine', patch: { unit: 'mending_vine' }, color: '#7be0a0' },
    { id: 'living_wall', patch: { unit: 'wall_vine', count: 2 }, color: '#d9c9a3' },
  ],
  // ---------------------------------------------------------------- Druid: flytrap
  devourer: [
    { id: 'acid_maw', patch: { unit: 'acid_trap' }, color: '#9ee37d' },
    { id: 'giant_maw', patch: { unit: 'giant_trap', duration: 18 }, color: '#8a2a4a' },
    { id: 'swift_maw', patch: { unit: 'swift_trap' }, color: '#ff9ab8' },
  ],
  venom_bush: [
    { id: 'spore_cloud', patch: { unit: 'spore_bush' }, color: '#b6e69a' },
    { id: 'paralytic_spores', patch: { unit: 'numbing_bush' }, color: '#bfefff' },
    { id: 'twin_bushes', patch: { count: 2 }, color: '#7fe07a' },
  ],
  lure_trap: [
    { id: 'sticky_lure', patch: { unit: 'sticky_lure' }, color: '#7fdcff' },
    { id: 'bright_lure', patch: { unit: 'bright_lure' }, color: '#fff2b3' },
    { id: 'thorned_lure', patch: { unit: 'thorned_lure' }, color: '#d3a86b' },
  ],
  // ---------------------------------------------------------------- Revenant: wail
  wail_legion: [
    { id: 'legion_swarm', patch: { count: 8, duration: 9 }, color: '#5a4aa8' },
    { id: 'legion_shield', mods: [{ stat: 'summonPower', inc: 0.2 }], flags: ['summonsInheritArmor'], color: '#9fb4c8' },
    { id: 'legion_haunt', patch: { unit: 'shade_haunter' }, color: '#c65cff' },
  ],
  wail_champion: [
    { id: 'champion_knight', patch: { unit: 'shade_knight' }, color: '#dfe9ff' },
    { id: 'champion_reaper', patch: { unit: 'shade_reaper' }, color: '#c0304f' },
    { id: 'champion_bond', patch: { duration: 9999, unique: true }, flags: ['bondRedirect'], color: '#ffe38a' },
  ],
  wail_dirge: [
    { id: 'dirge_choir', patch: { count: 3 }, color: '#6f5fd8' },
    { id: 'dirge_curse', patch: { unit: 'shade_curser' }, color: '#8a6aff' },
    { id: 'dirge_echo', patch: { duration: 8 }, cooldownMul: 0.6, color: '#b3a6ff' },
  ],
  // ---------------------------------------------------------------- Pilgrim: light volley
  volley_lance: [
    { id: 'lance_sun', patch: { damage: 4.2, radius: 20 }, color: '#fff2b3' },
    { id: 'lance_twin', patch: { count: 2, spread: 0.25 }, color: '#ffe38a' },
    { id: 'lance_smite', patch: { addStatus: [{ id: 'vulnerable', duration: 3 }] }, flags: ['holySmite'], color: '#ffd45c' },
  ],
  volley_rain: [
    { id: 'rain_storm', patch: { count: 18, damage: 0.6 }, color: '#ffd45c' },
    { id: 'rain_frost', patch: { addStatus: [{ id: 'chill', duration: 2, stacks: 1 }], tags: ['holy', 'frost', 'pierce', 'projectile'] }, color: '#bfefff' },
    { id: 'rain_return', patch: { pierce: 2 }, mods: [{ stat: 'projSpeed', inc: 0.2 }], color: '#fff7d6' },
  ],
  volley_mercy: [
    { id: 'mercy_ward', action: P({ count: 4, spread: 0.6, pierce: 1, damage: 0.7, speed: 640, range: 680, tags: ['holy', 'pierce', 'heal', 'projectile'], visual: 'arrow_light', onHit: { type: 'buff', target: 'allies', radius: 220, status: [{ id: 'shield', duration: 3, power: 0.15 }] } }), color: '#dfe9ff' },
    { id: 'mercy_flock', patch: { count: 7, spread: 1.0 }, color: '#ffe38a' },
    { id: 'mercy_zeal', mods: [{ stat: 'healPower', inc: 0.3 }, { stat: 'critChance', add: 0.05 }], color: '#fff0a8' },
  ],
  // ---------------------------------------------------------------- Hexer: hex
  hex_plague: [
    { id: 'plague_spread', patch: { radius: 160 }, flags: ['blightSpreads'], color: '#7fe07a' },
    { id: 'plague_rot', patch: { addStatus: [{ id: 'bleed', duration: 4, stacks: 2 }] }, color: '#c0304f' },
    { id: 'plague_bloom', patch: { healAllies: 0.3, duration: 3, tickRate: 2 }, color: '#b6e69a' },
  ],
  hex_gravity: [
    { id: 'gravity_well', patch: { pull: 1400, radius: 180, duration: 2, tickRate: 2 }, color: '#3d1fa0' },
    { id: 'gravity_crush', patch: { damage: 1.6, addStatus: [{ id: 'stun', duration: 0.4 }] }, color: '#8a7cff' },
    { id: 'gravity_echo', cooldownMul: 0.65, color: '#b3a6ff' },
  ],
  hex_dread: [
    { id: 'dread_silence', patch: { addStatus: [{ id: 'stun', duration: 1.0 }] }, color: '#5a3cff' },
    { id: 'dread_mark', mods: [{ stat: 'critChance', add: 0.1 }], flags: ['curseAmplify'], color: '#c65cff' },
    { id: 'dread_wide', patch: { radius: 220, duration: 1, tickRate: 1 }, color: '#8a6aff' },
  ],
  // ---------------------------------------------------------------- Warden: bulwark oath
  oath_iron: [
    { id: 'iron_wall', mods: [{ stat: 'armor', add: 30 }, { stat: 'thorns', add: 0.2 }], color: '#dfe9ff' },
    { id: 'iron_mirror', flags: ['thornsScaleArmor'], color: '#ffffff' },
    { id: 'iron_pulse', hooks: [{ on: 'damaged', cooldown: 3, action: { type: 'area', at: 'self', radius: 120, damage: 0.6, tags: ['blunt', 'area'], visual: 'quake' } }], color: '#b39b6a' },
  ],
  oath_dread: [
    { id: 'dread_chains', patch: { addStatus: [{ id: 'chill', duration: 4, stacks: 3 }] }, color: '#7fdcff' },
    { id: 'dread_terror', patch: { addStatus: [{ id: 'stun', duration: 0.6 }] }, color: '#5a3cff' },
    { id: 'dread_feast', mods: [{ stat: 'lifesteal', add: 0.08 }], color: '#c0304f' },
  ],
  oath_valor: [
    { id: 'valor_charge', hooks: [{ on: 'cast', tags: ['shield'], action: { type: 'dash', distance: 120, damage: 1.2, radius: 60, tags: ['blunt', 'movement'], visual: 'bash' } }], color: '#ffb35c' },
    { id: 'valor_banner', patch: { radius: 260 }, mods: [{ stat: 'auraRadius', inc: 0.3 }], color: '#ffd166' },
    { id: 'valor_rally', hooks: [{ on: 'cast', tags: ['shield'], action: { type: 'heal', target: 'allies', radius: 240, power: 0.4 } }], color: '#fff0a8' },
  ],
  // ---------------------------------------------------------------- Mystic: spirit pact
  pact_swarm: [
    { id: 'swarm_plague', patch: { unit: 'imp_plague' }, color: '#9ee37d' },
    { id: 'swarm_endless', patch: { count: 7, duration: 7 }, color: '#ff9ae8' },
    { id: 'swarm_bond', mods: [{ stat: 'summonPower', inc: 0.25 }], color: '#ff6ad5' },
  ],
  pact_fiend: [
    { id: 'fiend_tyrant', patch: { unit: 'fiend_tyrant', duration: 16 }, color: '#8a1a4a' },
    { id: 'fiend_bound', patch: { duration: 9999, unique: true }, flags: ['bondRedirect'], color: '#ffe38a' },
    { id: 'fiend_twin', patch: { count: 2, duration: 10 }, color: '#c23fa8' },
  ],
  pact_wraith: [
    { id: 'wraith_chill', patch: { unit: 'imp_wraith_frost' }, color: '#bfefff' },
    { id: 'wraith_pair', patch: { count: 2 }, color: '#d8c8ff' },
    { id: 'wraith_lasting', patch: { duration: 20 }, color: '#8a7cff' },
  ],
  // ---------------------------------------------------------------- Leomorph: tiger form
  form_bear: [
    { id: 'bear_mountain', mods: [{ stat: 'maxHp', inc: 0.2 }, { stat: 'armor', add: 15 }], color: '#8a6a4a' },
    { id: 'bear_maul', hooks: [{ on: 'hit', tags: ['slash'], chance: 0.3, cooldown: 1, action: { type: 'buff', target: 'enemies', radius: 1, status: [{ id: 'stun', duration: 0.4 }] } }], color: '#ff6a6a' },
    { id: 'bear_thick', patch: { addStatus: [{ id: 'regen', duration: 12, stacks: 3 }, { id: 'shield', duration: 6, power: 0.2 }] }, color: '#d9c9a3' },
  ],
  form_stalker: [
    { id: 'stalker_shadow', patch: { addStatus: [{ id: 'invuln', duration: 0.6 }] }, color: '#5a4a6a' },
    { id: 'stalker_hunt', mods: [{ stat: 'critChance', add: 0.12 }, { stat: 'moveSpeed', inc: 0.08 }], color: '#ffe0a0' },
    { id: 'stalker_frenzy', cooldownMul: 0.7, color: '#ff8c42' },
  ],
  form_lion: [
    { id: 'lion_pride', hooks: [{ on: 'cast', tags: ['movement'], action: summon('lion_cub', 2, 10) }], color: '#ffd166' },
    { id: 'lion_roar', patch: { radius: 280, addStatus: [{ id: 'weak', duration: 4 }] }, color: '#ffb347' },
    { id: 'lion_heart', hooks: [{ on: 'lowhp', hpBelow: 0.35, cooldown: 20, action: { type: 'heal', target: 'self', pct: 0.3 } }], color: '#ff9ab8' },
  ],
  // ---------------------------------------------------------------- Shaman: attunement
  attune_fury: [
    { id: 'fury_wave', patch: { radius: 180, damage: 1.1 }, color: '#ffd27a' },
    { id: 'fury_lingering', patch: { duration: 3, tickRate: 2, damage: 0.5 }, color: '#ff8c42' },
    { id: 'fury_shock', patch: { addStatus: [{ id: 'stun', duration: 0.5 }] }, color: '#a8f0ff' },
  ],
  attune_focus: [
    { id: 'focus_pierce', mods: [{ stat: 'pierce', add: 2 }], color: '#ffefc0' },
    { id: 'focus_crit', mods: [{ stat: 'critChance', add: 0.15 }, { stat: 'critMult', add: 0.3 }], color: '#ffd166' },
    { id: 'focus_swift', mods: [{ stat: 'attackSpeed', inc: 0.2 }], color: '#ffb347' },
  ],
  attune_twin: [
    { id: 'twin_storm', mods: [{ stat: 'projCount', add: 2 }, { stat: 'damage', inc: -0.15 }], color: '#c9f3ff' },
    { id: 'twin_split', mods: [{ stat: 'projSpeed', inc: 0.3 }], color: '#e8fbff' },
    { id: 'twin_echo', hooks: [{ on: 'cast', tags: ['fire', 'frost', 'lightning'], cooldown: 1, action: { type: 'area', at: 'self', radius: 100, damage: 0.4, tags: ['fire', 'frost', 'lightning', 'area'], visual: 'nova_shift' } }], color: '#ff8c42' },
  ],
  // ---------------------------------------------------------------- Goblins: turret
  turret_twin: [
    { id: 'twin_trio', patch: { count: 3, duration: 9 }, color: '#d2ff9a' },
    { id: 'twin_link', patch: { unit: 'gob_turret_linked' }, color: '#a8f0ff' },
    { id: 'twin_armored', patch: { unit: 'gob_turret_armored' }, color: '#9fb4c8' },
  ],
  turret_cannon: [
    { id: 'cannon_mortar', patch: { unit: 'gob_mortar' }, color: '#ff8c42' },
    { id: 'cannon_frost', patch: { unit: 'gob_cannon_frost' }, color: '#bfefff' },
    { id: 'cannon_siege', patch: { duration: 20 }, color: '#7fd63a' },
  ],
  turret_tesla: [
    { id: 'tesla_coil', patch: { unit: 'gob_tesla_coil' }, color: '#e8fbff' },
    { id: 'tesla_stun', patch: { unit: 'gob_tesla_stun' }, color: '#ffe38a' },
    { id: 'tesla_pair', patch: { count: 2 }, color: '#a8f0ff' },
  ],
  // ---------------------------------------------------------------- Bard: rallying chord
  chord_march: [
    { id: 'march_endless', patch: { status: [{ id: 'inspire', duration: 9 }, { id: 'haste', duration: 6 }] }, color: '#fff2b3' },
    { id: 'march_wide', patch: { radius: 340 }, color: '#ffd166' },
    { id: 'march_swift', cooldownMul: 0.7, color: '#b8ffb0' },
  ],
  chord_hymn: [
    { id: 'hymn_mend', action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 280, status: [{ id: 'inspire', duration: 7 }], cleanse: true, visual: 'heal' }, { type: 'heal', target: 'allies', radius: 280, power: 1.0 }] }, color: '#7be0a0' },
    { id: 'hymn_shield', patch: { addStatus: [{ id: 'shield', duration: 5, power: 0.25 }] }, color: '#dfe9ff' },
    { id: 'hymn_requiem', hooks: [{ on: 'kill', cooldown: 2, action: { type: 'heal', target: 'allies', radius: 300, power: 0.3 } }], color: '#c8b8ff' },
  ],
  chord_war: [
    { id: 'war_drums', patch: { addStatus: [{ id: 'fortify', duration: 6 }] }, color: '#d9c9a3' },
    { id: 'war_cry_bard', action: { type: 'multi', actions: [{ type: 'buff', target: 'allies', radius: 260, status: [{ id: 'might', duration: 6 }, { id: 'inspire', duration: 6 }], cleanse: true, visual: 'cry' }, { type: 'buff', target: 'enemies', at: 'self', radius: 260, status: [{ id: 'weak', duration: 5 }] }] }, color: '#ff9a3c' },
    { id: 'war_song', mods: [{ stat: 'duration', inc: 0.25 }], color: '#ffd166' },
  ],
};
